import { CSSProperties } from 'react';

type Props = {
  indicatorPosition: { x: number; y: number } | null;
  remainingPercent: number;
  remainingPh: string;
};

const TOOLTIP_WIDTH = 140;
const TOOLTIP_HEIGHT = 60;
const CIRCLE_SIZE = 238;
const INNER_RADIUS = 92;
const CIRCLE_CENTER = CIRCLE_SIZE / 2;
const TOOLTIP_OFFSET = 20;

const isPointInCircle = (x: number, y: number, centerX: number, centerY: number, radius: number): boolean => {
  const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
  return distance <= radius;
};

const doesRectIntersectCircle = (
  rectLeft: number,
  rectTop: number,
  rectRight: number,
  rectBottom: number,
  circleCenterX: number,
  circleCenterY: number,
  circleRadius: number
): boolean => {

  const corners = [
    [rectLeft, rectTop],
    [rectRight, rectTop],
    [rectRight, rectBottom],
    [rectLeft, rectBottom],
  ];


  for (const [x, y] of corners) {
    if (isPointInCircle(x, y, circleCenterX, circleCenterY, circleRadius)) {
      return true;
    }
  }

  if (
    circleCenterX >= rectLeft &&
    circleCenterX <= rectRight &&
    circleCenterY >= rectTop &&
    circleCenterY <= rectBottom
  ) {
    return true;
  }

  const closestX = Math.max(rectLeft, Math.min(circleCenterX, rectRight));
  const closestY = Math.max(rectTop, Math.min(circleCenterY, rectBottom));
  
  return isPointInCircle(closestX, closestY, circleCenterX, circleCenterY, circleRadius);
};

export const ProgressTooltip = ({ indicatorPosition, remainingPercent, remainingPh }: Props) => {
  if (!indicatorPosition) return null;

  const tooltipLeftFromLeft = indicatorPosition.x - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
  const tooltipTopFromLeft = indicatorPosition.y - TOOLTIP_HEIGHT / 2;
  const tooltipRightFromLeft = tooltipLeftFromLeft + TOOLTIP_WIDTH;
  const tooltipBottomFromLeft = tooltipTopFromLeft + TOOLTIP_HEIGHT;

  const intersectsFromLeft = doesRectIntersectCircle(
    tooltipLeftFromLeft,
    tooltipTopFromLeft,
    tooltipRightFromLeft,
    tooltipBottomFromLeft,
    CIRCLE_CENTER,
    CIRCLE_CENTER,
    INNER_RADIUS
  );

  const tooltipLeftFromRight = indicatorPosition.x + TOOLTIP_OFFSET;
  const tooltipTopFromRight = indicatorPosition.y - TOOLTIP_HEIGHT / 2;
  const tooltipRightFromRight = tooltipLeftFromRight + TOOLTIP_WIDTH;
  const tooltipBottomFromRight = tooltipTopFromRight + TOOLTIP_HEIGHT;

  const intersectsFromRight = doesRectIntersectCircle(
    tooltipLeftFromRight,
    tooltipTopFromRight,
    tooltipRightFromRight,
    tooltipBottomFromRight,
    CIRCLE_CENTER,
    CIRCLE_CENTER,
    INNER_RADIUS
  );

  let tooltipLeft: number;
  let tooltipTop: number;
  let isOnRight: boolean;

  if (!intersectsFromLeft && intersectsFromRight) {
    tooltipLeft = tooltipLeftFromLeft;
    tooltipTop = tooltipTopFromLeft;
    isOnRight = false;
  } else if (intersectsFromLeft && !intersectsFromRight) {
    tooltipLeft = tooltipLeftFromRight;
    tooltipTop = tooltipTopFromRight;
    isOnRight = true;
  } else {
    const isIndicatorOnRight = indicatorPosition.x > CIRCLE_CENTER;
    
    if (isIndicatorOnRight) {
      tooltipLeft = tooltipLeftFromLeft;
      tooltipTop = tooltipTopFromLeft;
      isOnRight = false;
    } else {
      tooltipLeft = tooltipLeftFromRight;
      tooltipTop = tooltipTopFromRight;
      isOnRight = true;
    }
  }

  let tooltipRight = tooltipLeft + TOOLTIP_WIDTH;
  let tooltipBottom = tooltipTop + TOOLTIP_HEIGHT;

  const finalIntersects = doesRectIntersectCircle(
    tooltipLeft,
    tooltipTop,
    tooltipRight,
    tooltipBottom,
    CIRCLE_CENTER,
    CIRCLE_CENTER,
    INNER_RADIUS
  );

  if (finalIntersects) {
    const closestX = Math.max(tooltipLeft, Math.min(CIRCLE_CENTER, tooltipRight));
    const closestY = Math.max(tooltipTop, Math.min(CIRCLE_CENTER, tooltipBottom));
    
    const distance = Math.sqrt(
      Math.pow(closestX - CIRCLE_CENTER, 2) + Math.pow(closestY - CIRCLE_CENTER, 2)
    );

    if (distance < INNER_RADIUS) {
      const dx = closestX - CIRCLE_CENTER;
      const dy = closestY - CIRCLE_CENTER;
      const length = Math.sqrt(dx * dx + dy * dy);
      
      const pushDistance = INNER_RADIUS - distance + 5;
      const pushX = (dx / length) * pushDistance;
      const pushY = (dy / length) * pushDistance;

      tooltipLeft += pushX;
      tooltipTop += pushY;
    }
  }

  const tooltipStyle: CSSProperties & Record<string, string> = {
    '--tooltip-x': `${tooltipLeft}px`,
    '--tooltip-y': `${tooltipTop}px`,
  };

  return (
    <div
      className="
        absolute
        pointer-events-none
        z-20
        left-[var(--tooltip-x)]
        top-[var(--tooltip-y)]
      "
      style={tooltipStyle}
    >
      <div className="relative rounded-[10px] bg-gray-475 px-4 py-[10px]">
        <div className="flex flex-col text-sm whitespace-nowrap">
          <span>Осталось: {remainingPercent.toFixed(0)}%</span>
          <span>({remainingPh} Ph)</span>
        </div>
        {isOnRight ? (
          <div
            className="absolute right-full top-1/2 -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #2C2C2C',
            }}
          />
        ) : (
          <div
            className="absolute left-full top-1/2 -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: '6px solid #2C2C2C',
            }}
          />
        )}
      </div>
    </div>
  );
};
